import React, { useState } from 'react'
import {
  Activity,
  Server,
  Users,
  RefreshCw,
  TrendingUp,
  AlertCircle,
  CheckCircle,
  XCircle,
} from 'lucide-react'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { useNavigate } from 'react-router-dom'
import toast from 'react-hot-toast'
import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
} from 'recharts'

import { Card, StatsCard, MetricCard } from '../components/ui/Card'
import Button from '../components/ui/Button'
import { StatusBadge } from '../components/ui/Badge'
import { formatNumber, timeAgo, formatDate } from '../utils/cn'

const COLORS = ['#2563eb', '#059669', '#d97706', '#7c3aed', '#64748b', '#dc2626']

const authHeaders = () => ({
  Authorization: `Bearer ${localStorage.getItem('auth_token') || ''}`,
})

const emptyStats = {
  providers: { total: 0, active: 0, inactive: 0 },
  nodes: { total: 0, active: 0, failed: 0 },
  syncs: { today: 0, total: 0, success_rate: 0, avg_duration_ms: 0 },
  sync_activity: [],
  node_distribution: [],
  provider_health: [],
  recent_sync_logs: [],
  system_metrics: [],
}

const Dashboard = () => {
  const [autoRefresh, setAutoRefresh] = useState(true)
  const queryClient = useQueryClient()
  const navigate = useNavigate()

  const { data = emptyStats, isLoading, error } = useQuery({
    queryKey: ['dashboard-stats'],
    queryFn: async () => {
      const response = await fetch('/api/v1/dashboard/stats', {
        headers: authHeaders(),
      })
      if (!response.ok) throw new Error('Failed to fetch dashboard statistics')
      return response.json()
    },
    refetchInterval: autoRefresh ? 30000 : false,
  })

  if (isLoading) {
    return (
      <div className="space-y-6 animate-pulse">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {[...Array(4)].map((_, i) => (
            <div key={i} className="h-32 rounded-xl bg-secondary-200 dark:bg-secondary-700" />
          ))}
        </div>
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <div className="lg:col-span-2 h-96 rounded-xl bg-secondary-200 dark:bg-secondary-700" />
          <div className="h-96 rounded-xl bg-secondary-200 dark:bg-secondary-700" />
        </div>
      </div>
    )
  }

  if (error) {
    return (
      <Card>
        <div className="text-center py-12">
          <AlertCircle className="w-10 h-10 text-error-500 mx-auto mb-3" />
          <h2 className="text-lg font-semibold text-secondary-900 dark:text-white">
            Failed to load dashboard
          </h2>
          <p className="text-secondary-500 dark:text-secondary-400 mt-2">
            The dashboard only shows measured values from the API. Please retry after the backend is available.
          </p>
          <Button className="mt-4" onClick={() => queryClient.invalidateQueries({ queryKey: ['dashboard-stats'] })}>
            Retry
          </Button>
        </div>
      </Card>
    )
  }

  const handleSyncAll = async () => {
    const providers = data.provider_health || []
    const enabledProviders = providers.filter((provider: any) => provider.status !== 'inactive')
    if (!enabledProviders.length) {
      toast.error('No enabled providers to sync')
      return
    }

    toast.loading(`Syncing ${enabledProviders.length} providers...`, { id: 'sync-all' })
    let synced = 0
    let failed = 0

    for (const provider of enabledProviders) {
      try {
        const response = await fetch(`/api/v1/providers/${provider.id}/sync`, {
          method: 'POST',
          headers: authHeaders(),
        })
        if (!response.ok) throw new Error(await response.text())
        synced += 1
      } catch {
        failed += 1
      }
    }

    toast.dismiss('sync-all')
    if (failed) {
      toast.error(`Sync finished with ${failed} failed provider(s)`)
    } else {
      toast.success(`Synced ${synced} provider(s)`)
    }
    queryClient.invalidateQueries({ queryKey: ['dashboard-stats'] })
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-secondary-900 dark:text-white">
            Dashboard Overview
          </h1>
          <p className="text-secondary-500 dark:text-secondary-400 mt-1">
            Values shown here are calculated from the database and current server state.
          </p>
        </div>
        <div className="flex items-center gap-3">
          <Button
            variant="secondary"
            onClick={() => setAutoRefresh(!autoRefresh)}
            leftIcon={<RefreshCw className="w-4 h-4" />}
          >
            {autoRefresh ? 'Auto Refresh ON' : 'Auto Refresh OFF'}
          </Button>
          <Button
            variant="primary"
            onClick={handleSyncAll}
            leftIcon={<RefreshCw className="w-4 h-4" />}
          >
            Sync All Providers
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-6">
        <StatsCard
          title="Total Providers"
          value={data.providers.total}
          icon={<Users className="w-6 h-6" />}
          color="primary"
          subtitle={`${data.providers.active} active, ${data.providers.inactive} inactive`}
        />

        <StatsCard
          title="Total Nodes"
          value={formatNumber(data.nodes.total)}
          icon={<Server className="w-6 h-6" />}
          color="success"
          subtitle={`${data.nodes.active} active, ${data.nodes.failed} failed/unsupported`}
        />

        <StatsCard
          title="Today's Syncs"
          value={data.syncs.today}
          icon={<RefreshCw className="w-6 h-6" />}
          color="warning"
          subtitle={`${data.syncs.total} total sync logs`}
        />

        <StatsCard
          title="Success Rate"
          value={`${Number(data.syncs.success_rate || 0).toFixed(1)}%`}
          icon={<TrendingUp className="w-6 h-6" />}
          color="info"
          subtitle={`Avg sync: ${Math.round((data.syncs.avg_duration_ms || 0) / 1000)}s`}
        />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <Card className="lg:col-span-2">
          <div className="flex items-center justify-between mb-6">
            <div>
              <h3 className="text-lg font-semibold text-secondary-900 dark:text-white">
                Sync Activity (24h)
              </h3>
              <p className="text-sm text-secondary-500 dark:text-secondary-400">
                Counted from sync log records in the last 24 hours
              </p>
            </div>
            <Button variant="ghost" size="sm" onClick={() => navigate('/sync-logs')}>
              View Details
            </Button>
          </div>
          <div className="h-72">
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={data.sync_activity || []}>
                <CartesianGrid strokeDasharray="3 3" stroke="var(--secondary-200)" />
                <XAxis dataKey="time" stroke="var(--secondary-500)" fontSize={12} />
                <YAxis stroke="var(--secondary-500)" fontSize={12} allowDecimals={false} />
                <Tooltip />
                <Area type="monotone" dataKey="success" stackId="1" stroke="#10b981" fill="#10b981" fillOpacity={0.2} name="Successful" />
                <Area type="monotone" dataKey="failed" stackId="1" stroke="#ef4444" fill="#ef4444" fillOpacity={0.2} name="Failed/Partial" />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </Card>

        <Card>
          <div className="mb-6">
            <h3 className="text-lg font-semibold text-secondary-900 dark:text-white">
              Node Distribution
            </h3>
            <p className="text-sm text-secondary-500 dark:text-secondary-400">
              Calculated from imported nodes
            </p>
          </div>
          <div className="h-72">
            {(data.node_distribution || []).length ? (
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie
                    data={data.node_distribution}
                    cx="50%"
                    cy="50%"
                    labelLine={false}
                    label={({ name, percent }) => `${name}: ${(percent * 100).toFixed(0)}%`}
                    outerRadius={80}
                    dataKey="value"
                  >
                    {data.node_distribution.map((entry: any, index: number) => (
                      <Cell key={entry.name} fill={COLORS[index % COLORS.length]} />
                    ))}
                  </Pie>
                  <Tooltip formatter={(value) => [`${value} nodes`, 'Count']} />
                </PieChart>
              </ResponsiveContainer>
            ) : (
              <div className="h-full flex items-center justify-center text-secondary-500 dark:text-secondary-400">
                No imported nodes
              </div>
            )}
          </div>
        </Card>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card>
          <div className="flex items-center justify-between mb-6">
            <div>
              <h3 className="text-lg font-semibold text-secondary-900 dark:text-white">
                Provider Health
              </h3>
              <p className="text-sm text-secondary-500 dark:text-secondary-400">
                Based on provider status, imported node counts, and sync logs
              </p>
            </div>
            <Button variant="primary" size="sm" onClick={() => navigate('/providers')} leftIcon={<Activity className="w-4 h-4" />}>
              Add Provider
            </Button>
          </div>

          <div className="space-y-4">
            {(data.provider_health || []).length ? data.provider_health.map((provider: any) => (
              <div key={provider.id} className="flex items-center justify-between p-4 rounded-lg border border-secondary-200 dark:border-secondary-700">
                <div className="flex items-center gap-4">
                  <div className="w-10 h-10 rounded-lg bg-primary-50 dark:bg-primary-900/30 flex items-center justify-center">
                    <Server className="w-5 h-5 text-primary-600 dark:text-primary-400" />
                  </div>
                  <div>
                    <h4 className="font-medium text-secondary-900 dark:text-white">{provider.name}</h4>
                    <div className="flex items-center gap-2 mt-1">
                      <span className="text-sm text-secondary-500 dark:text-secondary-400">{provider.nodes} nodes</span>
                      <span className="text-sm text-secondary-500 dark:text-secondary-400">|</span>
                      <span className="text-sm text-secondary-500 dark:text-secondary-400">{Number(provider.successRate || 0).toFixed(1)}% sync success</span>
                    </div>
                  </div>
                </div>

                <div className="flex items-center gap-4">
                  <div className="text-right">
                    <p className="text-sm text-secondary-500 dark:text-secondary-400">Last sync</p>
                    <p className="text-sm font-medium text-secondary-900 dark:text-white">
                      {provider.lastSync ? timeAgo(provider.lastSync) : 'Never'}
                    </p>
                  </div>
                  <StatusBadge status={provider.status as any} />
                </div>
              </div>
            )) : (
              <div className="text-center py-10 text-secondary-500 dark:text-secondary-400">No providers yet</div>
            )}
          </div>
        </Card>

        <Card>
          <div className="flex items-center justify-between mb-6">
            <div>
              <h3 className="text-lg font-semibold text-secondary-900 dark:text-white">
                Recent Syncs
              </h3>
              <p className="text-sm text-secondary-500 dark:text-secondary-400">
                Latest sync operations from the database
              </p>
            </div>
            <Button variant="ghost" size="sm" onClick={() => navigate('/sync-logs')}>
              View All
            </Button>
          </div>

          <div className="space-y-4">
            {(data.recent_sync_logs || []).length ? data.recent_sync_logs.map((sync: any) => {
              const icon = sync.status === 'success'
                ? <CheckCircle className="w-5 h-5 text-success-500" />
                : sync.status === 'warning'
                  ? <AlertCircle className="w-5 h-5 text-warning-500" />
                  : <XCircle className="w-5 h-5 text-error-500" />

              return (
                <div key={sync.id} className="flex items-center justify-between p-4 rounded-lg border border-secondary-200 dark:border-secondary-700">
                  <div className="flex items-center gap-4">
                    <div className="w-10 h-10 rounded-lg bg-secondary-100 dark:bg-secondary-800 flex items-center justify-center">
                      {icon}
                    </div>
                    <div>
                      <h4 className="font-medium text-secondary-900 dark:text-white">{sync.provider}</h4>
                      <div className="flex items-center gap-4 mt-1">
                        <span className="text-sm text-success-600 dark:text-success-400">{sync.nodesSynced} synced</span>
                        {sync.nodesFailed > 0 && <span className="text-sm text-error-600 dark:text-error-400">{sync.nodesFailed} failed</span>}
                        <span className="text-sm text-secondary-500 dark:text-secondary-400">{sync.duration}s</span>
                      </div>
                    </div>
                  </div>

                  <div className="text-right">
                    <p className="text-sm text-secondary-500 dark:text-secondary-400">
                      {sync.timestamp ? formatDate(sync.timestamp, { hour: '2-digit', minute: '2-digit' }) : 'N/A'}
                    </p>
                    <p className="text-sm font-medium text-secondary-900 dark:text-white">
                      {sync.timestamp ? timeAgo(sync.timestamp) : 'N/A'}
                    </p>
                  </div>
                </div>
              )
            }) : (
              <div className="text-center py-10 text-secondary-500 dark:text-secondary-400">No sync logs yet</div>
            )}
          </div>
        </Card>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        {(data.system_metrics || []).map((metric: any) => (
          <MetricCard
            key={metric.name}
            title={metric.name}
            value={metric.value}
            target={metric.target}
            progress={metric.progress}
            unit={metric.unit}
            status={metric.progress > 80 ? 'error' : metric.progress > 60 ? 'warning' : metric.progress > 40 ? 'info' : 'success'}
          />
        ))}
      </div>

      <Card>
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          <div>
            <h3 className="text-lg font-semibold text-secondary-900 dark:text-white">
              Data Source
            </h3>
            <p className="text-sm text-secondary-500 dark:text-secondary-400">
              Dashboard values are fetched from `/api/v1/dashboard/stats`; empty sections mean no real data has been recorded yet.
            </p>
          </div>
          <Button variant="secondary" leftIcon={<RefreshCw className="w-4 h-4" />} onClick={() => queryClient.invalidateQueries({ queryKey: ['dashboard-stats'] })}>
            Refresh Now
          </Button>
        </div>
      </Card>
    </div>
  )
}

export default Dashboard
